import {
  DayItem,
  GismeteoForecastResponse,
  SchedulePart,
  ScheduleRow,
  WeatherMap,
} from "../model/types";

export function utcToLocal(dateIso: string): Date {
  return new Date(dateIso);
}

export function degToWindDir(deg: number) {
  const dirs = [
    { t: "С", a: "↑" },
    { t: "СВ", a: "↗" },
    { t: "В", a: "→" },
    { t: "ЮВ", a: "↘" },
    { t: "Ю", a: "↓" },
    { t: "ЮЗ", a: "↙" },
    { t: "З", a: "←" },
    { t: "СЗ", a: "↖" },
  ];
  return dirs[Math.round(deg / 45) % 8];
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function pickAvg<T>(
  items: T[],
  fn: (item: T) => number | null | undefined
): number | null {
  const nums = items.map(fn).filter((v): v is number => typeof v === "number");

  if (nums.length === 0) return null;

  return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
}

const makeLabelWithOffset = (iso: string): string => {
  const utcDate = new Date(iso);
  const localMs = utcDate.getTime();
  const local = new Date(localMs);

  const hh = String(local.getHours()).padStart(2, "0");
  const mm = String(local.getMinutes()).padStart(2, "0");

  return `${hh}:${mm}`;
};

export function mapGismeteoPoint(
  label: string,
  p: {
    temp: number | null;
    feels: number | null;
    wind_speed: number | null;
    wind_gust: number | null;
    wind_dir: number | null;
    humidity: number | null;
    pressure: number | null;
    icon: string | null;
    description: string;
  }
): SchedulePart {
  const dir = p.wind_dir != null ? degToWindDir(p.wind_dir) : { t: "", a: "" };

  return {
    label,
    temp: p.temp,
    feelsLike: p.feels,
    wind: {
      speed: p.wind_speed,
      wind_gust: p.wind_gust,
      dirText: dir.t,
      arrow: dir.a,
    },
    humidity: p.humidity,
    pressureMm: p.pressure,
    iconCode: p.icon,
    iconUrl: null,
    conditionText: p.description,
    uvIndex: null, // у Gismeteo нет UV
  };
}

export function formatGismeteoTitle(
  dt: Date,
  variant: "full" | "short" = "full",
  today = new Date()
): string {
  const isSame = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (variant === "short") {
    if (isSame(dt, today)) return "Сегодня";
    if (isSame(dt, tomorrow)) return "Завтра";

    return new Intl.DateTimeFormat("ru-RU", {
      weekday: "short",
      day: "numeric",
    })
      .format(dt)
      .replace(".", "");
  }

  if (isSame(dt, today))
    return `Сегодня, ${dt.getDate()} ${dt.toLocaleString("ru-RU", {
      month: "long",
    })}`;

  if (isSame(dt, tomorrow))
    return `Завтра, ${dt.getDate()} ${dt.toLocaleString("ru-RU", {
      month: "long",
    })}`;

  return `${dt.toLocaleString("ru-RU", { weekday: "short" }).replace(".", "")}, ${dt.getDate()} ${dt.toLocaleString("ru-RU", { month: "long" })}`;
}

export function mapGismeteoFact(res: GismeteoForecastResponse) {
  const c = res.current;
  return mapGismeteoPoint("Сейчас", {
    temp: Math.round(c.temperature_air),
    feels: Math.round(c.temperature_heat_index),
    wind_speed: c.wind_speed,
    wind_gust: c.wind_gust,
    wind_dir: c.wind_direction,
    humidity: c.humidity,
    pressure: c.pressure,
    icon: c.icon_weather,
    description: c.description,
  });
}

export function mapGismeteoToSchedule(
  res: GismeteoForecastResponse,
  limitDays = 10
): ScheduleRow[] {
  const rows: ScheduleRow[] = [];

  const { forecast } = res;

  const indexes = forecast.time.map((iso, i) => ({
    date: utcToLocal(iso),
    index: i,
  }));

  const groups = new Map<string, { date: Date; indexes: number[] }>();

  for (const item of indexes) {
    const y = item.date.getFullYear();
    const m = String(item.date.getMonth() + 1).padStart(2, "0");
    const d = String(item.date.getDate()).padStart(2, "0");

    const key = `${y}-${m}-${d}`;

    if (!groups.has(key)) groups.set(key, { date: item.date, indexes: [] });
    groups.get(key)!.indexes.push(item.index);
  }

  const days = Array.from(groups.values()).slice(0, limitDays);

  for (const day of days) {
    const { date, indexes } = day;

    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");

    const dayIso = `${y}-${m}-${d}`;

    const hours = indexes.map((i) =>
      mapGismeteoPoint(makeLabelWithOffset(forecast.time[i]), {
        temp: Math.round(forecast.temperature_air[i]),
        feels: Math.round(forecast.temperature_heat_index[i]),
        wind_speed: forecast.wind_speed[i],
        wind_gust: forecast.wind_gust[i],
        wind_dir: forecast.wind_direction[i],
        humidity: forecast.humidity[i],
        pressure: forecast.pressure[i],
        icon: forecast.icon_weather[i],
        description: forecast.description[i],
      })
    );

    const parts = [
      {
        label: "Утром",
        hours: hours.filter((h) => {
          const hour = Number(h.label.split(":")[0]);
          return hour >= 5 && hour < 12;
        }),
      },
      {
        label: "Днём",
        hours: hours.filter((h) => {
          const hour = Number(h.label.split(":")[0]);
          return hour >= 12 && hour < 17;
        }),
      },
      {
        label: "Вечером",
        hours: hours.filter((h) => {
          const hour = Number(h.label.split(":")[0]);
          return hour >= 17 && hour < 22;
        }),
      },
      {
        label: "Ночью",
        hours: hours.filter((h) => {
          const hour = Number(h.label.split(":")[0]);
          return hour >= 22 || hour < 5;
        }),
      },
    ].map((p) => {
      const repr =
        p.hours.find((h) => h.iconCode != null) ?? p.hours[0] ?? null;

      const base = mapGismeteoPoint(p.label, {
        temp: pickAvg(p.hours, (h) => h.temp) ?? null,
        feels: pickAvg(p.hours, (h) => h.feelsLike) ?? null,
        wind_speed: pickAvg(p.hours, (h) => h.wind.speed),
        wind_gust: pickAvg(p.hours, (h) => h.wind.wind_gust),
        wind_dir: null,
        humidity: pickAvg(p.hours, (h) => h.humidity),
        pressure: pickAvg(p.hours, (h) => h.pressureMm),
        icon: repr?.iconCode ?? null,
        description: repr?.conditionText ?? p.label,
      });

      if (!repr) {
        return base;
      }

      return {
        ...base,
        wind: {
          ...base.wind,
          dirText: repr.wind.dirText,
          arrow: repr.wind.arrow,
        },
        iconCode: repr.iconCode,
        iconUrl: repr.iconUrl,
        conditionText: repr.conditionText,
        uvIndex: null,
      };
    });

    rows.push({
      dateIso: dayIso,
      titleFull: formatGismeteoTitle(date, "full"),
      titleShort: formatGismeteoTitle(date, "short"),
      parts,
      hours,
      footer: {
        avgHumidity: pickAvg(parts, (p) => p.humidity),
        avgPressureMm: pickAvg(parts, (p) => p.pressureMm),
        uvIndex: null,
      },
    });
  }

  return rows;
}

export function mapGismeteoToDays(
  res: GismeteoForecastResponse,
  limitDays = 30
): DayItem[] {
  const schedule = mapGismeteoToSchedule(res, limitDays);
  const today = new Date();

  return schedule.map((row) => {
    const dt = utcToLocal(row.dateIso + "T00:00:00Z");

    const ruDate = new Intl.DateTimeFormat("ru-RU", {
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    }).format(dt);

    let title = "";
    if (
      dt.getUTCFullYear() === today.getUTCFullYear() &&
      dt.getUTCMonth() === today.getUTCMonth() &&
      dt.getUTCDate() === today.getUTCDate()
    ) {
      title = "Сегодня";
    } else if (
      dt.getUTCFullYear() === today.getUTCFullYear() &&
      dt.getUTCMonth() === today.getUTCMonth() &&
      dt.getUTCDate() === today.getUTCDate() + 1
    ) {
      title = "Завтра";
    } else {
      title = capitalize(
        new Intl.DateTimeFormat("ru-RU", {
          weekday: "long",
          timeZone: "UTC",
        }).format(dt)
      );
    }

    const fmt = (n: number | null) =>
      n == null ? "—" : `${n > 0 ? "+" : ""}${n}°`;

    return {
      iso: row.dateIso,
      dateText: ruDate,
      title,
      periodsLabel: undefined,
      argTempFells: pickAvg(row.parts, (h) => h.feelsLike),
      argTemp: pickAvg(row.parts, (h) => h.temp),
      dayTemp: fmt(row.parts[1]?.temp),
      nightTemp: fmt(row.parts[3]?.temp),
    };
  });
}

export function makeWeatherMapFromSchedule(rows: DayItem[]): WeatherMap {
  const map: WeatherMap = {};

  const pickTempString = (n: number | null | undefined) => {
    if (n == null) return undefined;
    return `${n > 0 ? "+" : ""}${n}°`;
  };

  for (const row of rows) {
    const parts = row;
    map[parts.iso] = {
      temp: pickTempString(parts?.argTemp),
      feels: pickTempString(parts?.argTempFells),
    };
  }

  return map;
}
